import { HabitService, type Habit, type HabitBoardCard } from '../../services/habitService'
import type { TaskStep } from '../../services/taskService'
import { StepsList } from '../ui/StepsList'

/**
 * A habit's steps — what each time consists of (owner 2026-09-06: "like
 * Steps for a task, it should be a list and it should allow edits and
 * additions"), and today's ticks when the habit has a time today (owner,
 * later: "go ahead with per-occurrence step ticking"). The list is the
 * definition; the ticks belong to the day and live on that occurrence.
 */
interface Props {
  habit: Habit
  card: HabitBoardCard | null
  todayKey: string
  onChanged: (habit: Habit) => void
  onCard: (card: HabitBoardCard | null) => void
  onBreakdown?: () => Promise<void> | void
  breakdownBusy?: boolean
  live: boolean
  onAllDone?: () => void
}

export function HabitSteps({ habit, card, todayKey, onChanged, onCard, onBreakdown, breakdownBusy = false, live, onAllDone }: Props) {
  const svc = new HabitService()
  const steps: TaskStep[] = habit.steps || []
  const hasAi = Boolean(habit.has_ai_breakdown) || steps.some((s) => s.source === 'ai')
  const today = live ? card?.today || null : null
  const ticked = new Set<string>(today?.steps_done || [])
  const save = async (next: TaskStep[]) => {
    await svc.updateHabit(String(habit.id), { steps: next })
    onChanged(await svc.getHabit(String(habit.id)))
  }
  const tick = async (id: string, done: boolean) => {
    const next = done ? [...ticked, id] : [...ticked].filter((x) => x !== id)
    const r = await svc.markToday(habit.id, null, todayKey, undefined, next)
    onCard(r.board)
  }
  const allTicked = Boolean(today) && steps.length > 0 && steps.every((s) => ticked.has(s.id))
  return (
    <div>
      <StepsList
        steps={steps}
        onSave={save}
        open={live}
        tickable={Boolean(today)}
        tickedIds={today ? ticked : undefined}
        onTick={today ? tick : undefined}
        scope={today ? 'today' : undefined}
        hasAi={hasAi}
        onBreakdown={onBreakdown}
        breakdownBusy={breakdownBusy}
        storeKey={`habit.steps.open.${habit.id}`}
        testId="habit-steps"
        title="Each time, the steps"
        emptyWords="No steps written down; the habit is the whole of it."
      />
      {allTicked && today?.outcome === 'pending' && onAllDone && (
        <p className="mt-1 pl-6 text-app-meta text-secondary" data-testid="habit-steps-all-done">
          Every step ticked. <button type="button" onClick={onAllDone} className="font-medium text-brand hover:underline">Mark today done</button>
        </p>
      )}
    </div>
  )
}

export default HabitSteps
